import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Button,
  Text,
  Stack,
  VStack,
  Toaster,
  SimpleGrid,
  Checkbox,
  Badge,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Upload, Search, FolderPlus, File, Folder } from 'lucide-react'
import { useProperties } from '@/features/properties/hooks/useProperties'
import {
  useDocuments,
  useDocumentCountByResidence,
  useDocumentCountByFolder,
  useMoveDocument,
  useDeleteDocument,
  useDownloadDocument,
} from '../hooks/useDocuments'
import {
  useFolderTree,
  useCreateDefaultFolders,
  useDeleteFolder,
  useSubfolderCounts,
} from '../hooks/useFolders'
import { useCoOwnerFolders, isCoOwnerSystemFolder } from '../hooks/useCoOwnerFolders'
import { FolderModal } from '../components/FolderModal'
import { DocumentUploadModal } from '../components/DocumentUploadModal'
import { DocumentSearchDrawer } from '../components/DocumentSearchDrawer'
import { DocumentPreviewDrawer } from '../components/DocumentPreviewDrawer'
import { DocumentCard } from '../components/DocumentCard'
import { ResidenceCard } from '../components/ResidenceCard'
import { FolderCard } from '../components/FolderCard'
import { CoOwnerFolderCard } from '../components/CoOwnerFolderCard'
import { DocumentsBreadcrumb, type BreadcrumbItem } from '../components/DocumentsBreadcrumb'
import { CollapsibleDocumentsSidebar } from '../components/CollapsibleDocumentsSidebar'
import { buildFolderPath } from '../utils/folderUtils'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EmptyState } from '@/shared/components/EmptyState'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'
import { toaster } from '@/shared/lib/toaster'
import type { DocumentCategory, DocumentFilters, DocumentWithRelations } from '../types'

// Draggable document wrapper component (HTML5 native)
interface DraggableDocumentProps {
  document: DocumentWithRelations
  showResidenceInfo: boolean
  isActive: boolean
  onView: (doc: DocumentWithRelations) => void
  onEdit: (doc: DocumentWithRelations) => void
  onDownload: (doc: DocumentWithRelations) => void
  onDelete: (doc: DocumentWithRelations) => void
}

function DraggableDocument({
  document,
  showResidenceInfo,
  isActive,
  onView,
  onEdit,
  onDownload,
  onDelete,
}: DraggableDocumentProps) {
  return (
    <DocumentCard
      document={document}
      showResidenceInfo={showResidenceInfo}
      draggable={true}
      isActive={isActive}
      onView={onView}
      onEdit={onEdit}
      onDownload={onDownload}
      onDelete={onDelete}
    />
  )
}

export function DocumentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { residenceId, folderId, docId } = useParams<{
    residenceId?: string
    folderId?: string
    docId?: string
  }>()

  const { data: properties = [], isLoading: isLoadingProperties } = useProperties()

  // Navigation state derived from URL
  const selectedResidenceId = residenceId
  const currentFolderId = folderId || null

  // UI state
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory>()
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false)
  const [searchFilters, setSearchFilters] = useState<DocumentFilters>({})

  // Folder management states
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false)
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'edit'>('create')
  const [parentFolderIdForCreate, setParentFolderIdForCreate] = useState<string>()
  const [editFolderId, setEditFolderId] = useState<string>()
  const [folderToDelete, setFolderToDelete] = useState<string>()

  // Document management states
  const [uploadModalMode, setUploadModalMode] = useState<'create' | 'edit'>('create')
  const [editDocument, setEditDocument] = useState<DocumentWithRelations>()
  const [documentToDelete, setDocumentToDelete] = useState<DocumentWithRelations>()
  const [previewDocument, setPreviewDocument] = useState<DocumentWithRelations | null>(null)

  // Co-owner folders state
  const [showInactiveCoOwners, setShowInactiveCoOwners] = useState(false)

  // Hooks
  const {
    data: folderTree = [],
    folders = [],
    isLoading: isLoadingFolders,
  } = useFolderTree(selectedResidenceId)
  const createDefaultFolders = useCreateDefaultFolders()
  const deleteFolder = useDeleteFolder()
  const moveDocument = useMoveDocument()
  const deleteDocument = useDeleteDocument()
  const downloadDocument = useDownloadDocument()

  // Count hooks
  const residenceIds = properties.map((p) => p.id)
  const { data: documentCountByResidence = {} } = useDocumentCountByResidence(residenceIds)
  const { data: documentCountByFolder = {} } = useDocumentCountByFolder(selectedResidenceId)
  const subfolderCounts = useSubfolderCounts(selectedResidenceId)

  // Initialize default folders for new residences
  useEffect(() => {
    if (selectedResidenceId && folderTree.length === 0 && !isLoadingFolders) {
      createDefaultFolders.mutate(selectedResidenceId)
    }
  }, [selectedResidenceId, folderTree.length, isLoadingFolders])

  // Detect if we're in the co-owners system folder
  const currentFolder = useMemo(() => {
    if (!currentFolderId) return null
    return folders.find((f) => f.id === currentFolderId)
  }, [currentFolderId, folders])

  const isInCoOwnerSystemFolder = useMemo(() => {
    return currentFolder ? isCoOwnerSystemFolder(currentFolder) : false
  }, [currentFolder])

  // Get co-owner folders (toujours charger pour afficher le chevron dans la sidebar)
  const { data: coOwnerFolders = [] } = useCoOwnerFolders(
    selectedResidenceId,
    showInactiveCoOwners
  )

  // Get current folder's subfolders
  const currentSubfolders = useMemo(() => {
    return folders.filter((f) => f.parent_folder_id === currentFolderId)
  }, [folders, currentFolderId])

  // Combine tree selection with search filters
  const activeFilters: DocumentFilters = {
    ...searchFilters,
    residence_id: selectedResidenceId,
    category: selectedCategory,
    folder_id: currentFolderId || undefined,
    status: 'active',
    page: 1,
    page_size: 50,
  }

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error,
    refetch,
  } = useDocuments(activeFilters)

  const documents = documentsData?.data || []

  // Compute folder navigation path from URL (no state needed!)
  const folderNavigationPath = useMemo(() => {
    if (currentFolderId && folders.length > 0) {
      return buildFolderPath(currentFolderId, folders, coOwnerFolders)
    }
    return []
  }, [currentFolderId, folders, coOwnerFolders])

  // Initialize preview from URL docId
  useEffect(() => {
    if (docId) {
      // Only update if the doc ID changed (not if documents array reference changed)
      if (previewDocument?.id !== docId) {
        const doc = documents.find((d) => d.id === docId)
        if (doc) {
          setPreviewDocument(doc)
        }
      }
    } else if (previewDocument !== null) {
      setPreviewDocument(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId])

  // Build breadcrumb
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const items: BreadcrumbItem[] = [
      {
        label: t('documents.residences'),
        onClick: () => {
          navigate('/app/documents')
        },
      },
    ]

    if (selectedResidenceId) {
      const residence = properties.find((p) => p.id === selectedResidenceId)
      items.push({
        label: residence?.name || '',
        onClick: () => {
          navigate(`/app/documents/residences/${selectedResidenceId}`)
        },
      })

      // Add folder path
      folderNavigationPath.forEach((folder, index) => {
        items.push({
          label: folder.name,
          onClick:
            index < folderNavigationPath.length - 1
              ? () => {
                  if (folder.id) {
                    navigate(
                      `/app/documents/residences/${selectedResidenceId}/folders/${folder.id}`
                    )
                  }
                }
              : undefined,
        })
      })
    }

    return items
  }, [selectedResidenceId, properties, folderNavigationPath, t, navigate])

  const handleSelectResidence = (residenceId: string) => {
    navigate(`/app/documents/residences/${residenceId}`)
    setSelectedCategory(undefined)
  }

  const handleSelectFolder = (folderId: string, _folderName: string) => {
    if (!selectedResidenceId) return
    navigate(`/app/documents/residences/${selectedResidenceId}/folders/${folderId}`)
  }

  const handleSearch = (filters: DocumentFilters) => {
    setSearchFilters(filters)
    if (filters.residence_id) {
      navigate(`/app/documents/residences/${filters.residence_id}`)
    }
    setSelectedCategory(filters.category as DocumentCategory | undefined)
  }

  // Folder handlers
  const handleCreateSubfolder = (parentFolderId: string) => {
    setParentFolderIdForCreate(parentFolderId)
    setFolderModalMode('create')
    setIsFolderModalOpen(true)
  }

  const handleEditFolder = (folderId: string) => {
    setEditFolderId(folderId)
    setFolderModalMode('edit')
    setIsFolderModalOpen(true)
  }

  const handleDeleteFolderConfirm = async () => {
    if (!folderToDelete) return

    try {
      await deleteFolder.mutateAsync(folderToDelete)
      toaster.create({
        title: 'Dossier supprimé',
        description: 'Le dossier a été supprimé avec succès',
        type: 'success',
      })
      setFolderToDelete(undefined)
    } catch (error) {
      console.error('Error deleting folder:', error)
      toaster.create({
        title: 'Impossible de supprimer le dossier',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      })
    }
  }

  // Handle document drop on folder
  const handleDropDocument = async (documentId: string, folderId: string | null) => {
    try {
      await moveDocument.mutateAsync({ documentId, folderId })
      // Success - optimistic update already made it feel instant
    } catch (error) {
      toaster.create({
        title: 'Échec du déplacement',
        description: 'Le document n\'a pas pu être déplacé. Rafraîchissez la page.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  // Document navigation
  const getCurrentDocumentIndex = () => {
    if (!previewDocument) return -1
    return documents.findIndex((d) => d.id === previewDocument.id)
  }

  const handleNavigateDocumentPrevious = () => {
    const currentIndex = getCurrentDocumentIndex()
    if (currentIndex > 0) {
      const prevDoc = documents[currentIndex - 1]
      if (prevDoc && selectedResidenceId) {
        if (currentFolderId) {
          navigate(`/app/documents/residences/${selectedResidenceId}/folders/${currentFolderId}/documents/${prevDoc.id}`)
        } else {
          navigate(`/app/documents/residences/${selectedResidenceId}/documents/${prevDoc.id}`)
        }
      }
    }
  }

  const handleNavigateDocumentNext = () => {
    const currentIndex = getCurrentDocumentIndex()
    if (currentIndex < documents.length - 1) {
      const nextDoc = documents[currentIndex + 1]
      if (nextDoc && selectedResidenceId) {
        if (currentFolderId) {
          navigate(`/app/documents/residences/${selectedResidenceId}/folders/${currentFolderId}/documents/${nextDoc.id}`)
        } else {
          navigate(`/app/documents/residences/${selectedResidenceId}/documents/${nextDoc.id}`)
        }
      }
    }
  }

  // Keyboard navigation
  useEffect(() => {
    if (!previewDocument) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const currentIndex = documents.findIndex((d) => d.id === previewDocument.id)
        if (currentIndex > 0) {
          const prevDoc = documents[currentIndex - 1]
          if (prevDoc) {
            setPreviewDocument(prevDoc)
          }
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        const currentIndex = documents.findIndex((d) => d.id === previewDocument.id)
        if (currentIndex < documents.length - 1) {
          const nextDoc = documents[currentIndex + 1]
          if (nextDoc) {
            setPreviewDocument(nextDoc)
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewDocument, documents])

  // Document handlers
  const handleCreateDocument = () => {
    setUploadModalMode('create')
    setEditDocument(undefined)
    setIsUploadModalOpen(true)
  }

  const handleViewDocument = (document: DocumentWithRelations) => {
    if (!selectedResidenceId) return

    // Preserve folder context in URL if we're in a folder
    if (currentFolderId) {
      navigate(`/app/documents/residences/${selectedResidenceId}/folders/${currentFolderId}/documents/${document.id}`)
    } else {
      navigate(`/app/documents/residences/${selectedResidenceId}/documents/${document.id}`)
    }
  }

  const handleEditDocument = (document: DocumentWithRelations) => {
    setEditDocument(document)
    setUploadModalMode('edit')
    setIsUploadModalOpen(true)
  }

  const handleDownloadDocument = async (document: DocumentWithRelations) => {
    try {
      await downloadDocument.mutateAsync(document)
      toaster.create({
        title: 'Téléchargement réussi',
        description: `Le fichier "${document.original_filename}" a été téléchargé`,
        type: 'success',
      })
    } catch (error) {
      console.error('Download error:', error)
      toaster.create({
        title: 'Échec du téléchargement',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      })
    }
  }

  const handleDeleteDocument = (document: DocumentWithRelations) => {
    setDocumentToDelete(document)
  }

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete) return

    try {
      await deleteDocument.mutateAsync(documentToDelete.id)
      toaster.create({
        title: 'Document supprimé',
        description: 'Le document a été supprimé avec succès',
        type: 'success',
      })

      // Close drawer if the deleted document was being previewed
      if (previewDocument?.id === documentToDelete.id) {
        setPreviewDocument(null)
      }

      setDocumentToDelete(undefined)
    } catch (error) {
      console.error('Error deleting document:', error)
      toaster.create({
        title: 'Impossible de supprimer le document',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      })
    }
  }

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false)
    setUploadModalMode('create')
    setEditDocument(undefined)
  }

  if (isLoadingProperties) {
    return <LoadingState message={t('common.loading')} />
  }

  if (properties.length === 0) {
    return (
      <EmptyState
        title={t('documents.no_residences')}
        description={t('documents.create_residence_first')}
      />
    )
  }

  // View: Residence selection (grid of cards)
  const renderResidenceSelection = () => (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg">{t('documents.title')}</Heading>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {properties.length} résidence{properties.length !== 1 ? 's' : ''}
          </Text>
        </Box>
        <HStack gap={2}>
          <Button variant="outline" onClick={() => setIsSearchDrawerOpen(true)}>
            <HStack gap={2}>
              <Search size={18} />
              <span>{t('documents.search')}</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} gap={4}>
        {properties.map((property) => (
          <ResidenceCard
            key={property.id}
            residence={property}
            documentCount={documentCountByResidence[property.id] || 0}
            onClick={() => handleSelectResidence(property.id)}
          />
        ))}
      </SimpleGrid>
    </Box>
  )

  // View: Folder/Document navigation (Google Drive style)
  const renderFolderView = () => (
    <Box>
      <Grid templateColumns="auto 1fr" gap={4}>
        {/* Header row spanning both columns */}
        <GridItem colSpan={2} mb={4}>
          <HStack justify="space-between">
            <DocumentsBreadcrumb items={breadcrumbItems} />
            <HStack gap={2} flexShrink={0}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setParentFolderIdForCreate(currentFolderId || undefined)
                  setFolderModalMode('create')
                  setIsFolderModalOpen(true)
                }}
              >
                <HStack gap={2}>
                  <FolderPlus size={18} />
                  <span>Nouveau dossier</span>
                </HStack>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsSearchDrawerOpen(true)}>
                <HStack gap={2}>
                  <Search size={18} />
                  <span>{t('documents.search')}</span>
                </HStack>
              </Button>
              <Button colorPalette="brand" size="sm" onClick={handleCreateDocument}>
                <HStack gap={2}>
                  <Upload size={18} />
                  <span>{t('documents.upload')}</span>
                </HStack>
              </Button>
            </HStack>
          </HStack>
        </GridItem>
        <GridItem>
          <CollapsibleDocumentsSidebar
            residenceId={selectedResidenceId!}
            folders={folders}
            folderTree={folderTree}
            currentFolderId={currentFolderId}
            coOwnerFolders={coOwnerFolders}
            onSelectFolder={(folderId) => {
              if (folderId) {
                navigate(`/app/documents/residences/${selectedResidenceId}/folders/${folderId}`)
              } else {
                navigate(`/app/documents/residences/${selectedResidenceId}`)
              }
            }}
            onCreateSubfolder={handleCreateSubfolder}
            onEditFolder={handleEditFolder}
            onDeleteFolder={(id) => setFolderToDelete(id)}
            onDrop={() => {}}
            onDropDocument={handleDropDocument}
          />
        </GridItem>

        <GridItem>
          <Stack gap={6}>
            {/* Co-owner folders (symbolic links) */}
            {isInCoOwnerSystemFolder && coOwnerFolders.length > 0 && (
              <Box>
                <HStack
                  justify="space-between"
                  mb={4}
                  pb={3}
                  borderBottomWidth="2px"
                  borderBottomColor="gray.200"
                >
                  <HStack gap={2}>
                    <Folder size={18} color="#4A5568" />
                    <Text fontSize="md" fontWeight="semibold" color="gray.700">
                      Dossiers
                    </Text>
                    <Badge colorPalette="gray" size="sm">
                      {coOwnerFolders.length}
                    </Badge>
                  </HStack>
                  <Checkbox.Root
                    checked={showInactiveCoOwners}
                    onCheckedChange={(e) => setShowInactiveCoOwners(!!e.checked)}
                  >
                    <Checkbox.Control />
                    <Checkbox.Label>
                      <Text fontSize="sm">Afficher les anciens</Text>
                    </Checkbox.Label>
                    <Checkbox.HiddenInput />
                  </Checkbox.Root>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} gap={4}>
                  {coOwnerFolders.map((coOwnerLink) => (
                    <CoOwnerFolderCard
                      key={coOwnerLink.folder_id}
                      coOwnerLink={coOwnerLink}
                      documentCount={
                        coOwnerLink.linked_folder_id
                          ? documentCountByFolder[coOwnerLink.linked_folder_id] || 0
                          : 0
                      }
                      onClick={() => {
                        // Navigate to the physical folder
                        if (coOwnerLink.linked_folder_id && selectedResidenceId) {
                          navigate(
                            `/app/documents/residences/${selectedResidenceId}/folders/${coOwnerLink.linked_folder_id}`
                          )
                        }
                      }}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Regular subfolders */}
            {!isInCoOwnerSystemFolder && currentSubfolders.length > 0 && (
              <Box>
                <HStack
                  mb={4}
                  pb={3}
                  borderBottomWidth="2px"
                  borderBottomColor="gray.200"
                  gap={2}
                >
                  <Folder size={18} color="#4A5568" />
                  <Text fontSize="md" fontWeight="semibold" color="gray.700">
                    Dossiers
                  </Text>
                  <Badge colorPalette="gray" size="sm">
                    {currentSubfolders.length}
                  </Badge>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} gap={4}>
                  {currentSubfolders.map((folder) => (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      documentCount={documentCountByFolder[folder.id] || 0}
                      subfolderCount={subfolderCounts[folder.id] || 0}
                      onClick={() => handleSelectFolder(folder.id, folder.name)}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Documents - Always show with title */}
            <Box>
                <HStack
                  mb={4}
                  pb={3}
                  borderBottomWidth="2px"
                  borderBottomColor="gray.200"
                  gap={2}
                >
                  <File size={18} color="#4A5568" />
                  <Text fontSize="md" fontWeight="semibold" color="gray.700">
                    Fichiers
                  </Text>
                  {documents.length > 0 && (
                    <Badge colorPalette="gray" size="sm">
                      {documents.length}
                    </Badge>
                  )}
                </HStack>
                {isLoadingDocuments ? (
                  <LoadingState message={t('documents.loading')} />
                ) : error ? (
                  <ErrorState message={t('common.error')} onRetry={refetch} />
                ) : documents.length === 0 ? (
                  <EmptyState
                    title={
                      currentFolderId
                        ? 'Aucun document dans ce dossier'
                        : currentSubfolders.length > 0
                        ? 'Aucun document à la racine'
                        : 'Aucun document dans cette résidence'
                    }
                    description={
                      currentFolderId
                        ? 'Ajoutez des documents à ce dossier en cliquant sur le bouton ci-dessous'
                        : currentSubfolders.length > 0
                        ? 'Les documents sont organisés dans les dossiers ci-dessus. Vous pouvez également ajouter des documents à la racine.'
                        : 'Commencez par ajouter des documents à cette résidence'
                    }
                    action={{
                      label: 'Ajouter un document',
                      onClick: handleCreateDocument,
                    }}
                  />
                ) : currentFolderId ? (
                // Drag to folder enabled when inside a folder
                <Stack gap={3}>
                  {documents.map((doc) => (
                    <DraggableDocument
                      key={doc.id}
                      document={doc}
                      showResidenceInfo={false}
                      isActive={previewDocument?.id === doc.id}
                      onView={handleViewDocument}
                      onEdit={handleEditDocument}
                      onDownload={handleDownloadDocument}
                      onDelete={handleDeleteDocument}
                    />
                  ))}
                </Stack>
              ) : (
                // No drag & drop in aggregated view (all documents)
                <Stack gap={3}>
                  {documents.map((doc) => (
                    <DocumentCard
                      key={doc.id}
                      document={doc}
                      showResidenceInfo={true}
                      draggable={false}
                      isActive={previewDocument?.id === doc.id}
                      onView={handleViewDocument}
                      onEdit={handleEditDocument}
                      onDownload={handleDownloadDocument}
                      onDelete={handleDeleteDocument}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </GridItem>
      </Grid>
    </Box>
  )

  return (
    <Box>
      {!selectedResidenceId ? renderResidenceSelection() : renderFolderView()}

      {/* Upload/Edit Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        mode={uploadModalMode}
        editDocument={editDocument}
        preselectedResidenceId={selectedResidenceId}
        preselectedFolderId={currentFolderId || undefined}
        preselectedCategory={selectedCategory}
      />

      {/* Search Drawer */}
      <DocumentSearchDrawer
        isOpen={isSearchDrawerOpen}
        onClose={() => setIsSearchDrawerOpen(false)}
        onSearch={handleSearch}
        currentFilters={activeFilters}
      />

      {/* Folder Modal */}
      <FolderModal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false)
          setEditFolderId(undefined)
          setParentFolderIdForCreate(undefined)
        }}
        mode={folderModalMode}
        residenceId={selectedResidenceId!}
        editFolderId={editFolderId}
        parentFolderId={parentFolderIdForCreate}
      />

      {/* Confirm Delete Folder Dialog */}
      {folderToDelete && (
        <ConfirmDeleteDialog
          isOpen={true}
          onClose={() => setFolderToDelete(undefined)}
          onConfirm={handleDeleteFolderConfirm}
          entityName={folders.find((f) => f.id === folderToDelete)?.name || ''}
          entityType="dossier"
          warningMessage={`Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible. Le dossier doit être vide (aucun document ni sous-dossier).`}
        />
      )}

      {/* Confirm Delete Document Dialog */}
      {documentToDelete && (
        <ConfirmDeleteDialog
          isOpen={true}
          onClose={() => setDocumentToDelete(undefined)}
          onConfirm={handleDeleteDocumentConfirm}
          entityName={documentToDelete.display_name}
          entityType="document"
          warningMessage={`Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.`}
        />
      )}

      {/* Document Preview Drawer */}
      <DocumentPreviewDrawer
        document={previewDocument}
        isOpen={!!previewDocument}
        onClose={() => {
          // Navigate back: remove /documents/:docId from URL
          if (currentFolderId) {
            navigate(`/app/documents/residences/${selectedResidenceId}/folders/${currentFolderId}`)
          } else if (selectedResidenceId) {
            navigate(`/app/documents/residences/${selectedResidenceId}`)
          } else {
            navigate('/app/documents')
          }
        }}
        onNavigatePrevious={handleNavigateDocumentPrevious}
        onNavigateNext={handleNavigateDocumentNext}
        hasPrevious={getCurrentDocumentIndex() > 0}
        hasNext={getCurrentDocumentIndex() < documents.length - 1}
        onEdit={handleEditDocument}
        onDownload={handleDownloadDocument}
        onDelete={handleDeleteDocument}
      />

      {/* Toaster for notifications */}
      <Toaster toaster={toaster}>
        {(toast) => (
          <Box
            bg={
              toast.type === 'success' ? 'green.500' : toast.type === 'error' ? 'red.500' : 'blue.500'
            }
            color="white"
            p={4}
            borderRadius="md"
            boxShadow="lg"
          >
            <VStack align="start" gap={1}>
              {toast.title && <Text fontWeight="bold">{toast.title}</Text>}
              {toast.description && <Text fontSize="sm">{toast.description}</Text>}
            </VStack>
          </Box>
        )}
      </Toaster>
    </Box>
  )
}
